import React from 'react';
import * as Sentry from '@sentry/react';
import type { Entity, EntityMatch } from '@/domain/document-processing/types/entity-types';
import { AlertTriangle, ChevronDown, FileText } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import { ErrorFallback } from '@/components/error-boundary/ErrorFallback';
import type { MatchSighting } from '@/domain/document-processing/utils/entity-match-evidence';

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

const normalise = (value: string) => value.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();

function additionalFields(match: EntityMatch): Array<{ label: string; values: string[] }> {
  const rich = match.richDetails ?? {};
  const description = text(match.details) ?? '';
  const labelled = new Map<string, Set<string>>();
  // Only a repeated value under the same explicit label is known to be redundant.
  for (const segment of description.split(/\s+[-–—]\s+/)) {
    const colon = segment.indexOf(':');
    if (colon < 0) continue;
    labelled.set(normalise(segment.slice(0, colon)), new Set(segment.slice(colon + 1).split(/[,;]/).map(normalise)));
  }
  const schema = text(rich.schema);
  const name = text(rich.name);
  const schemaPrefix = name && schema ? normalise(`Match found: ${name} (${schema})`) : undefined;
  // This exact format is produced by the existing screening service for entity type.
  const schemaAlreadyShown = schemaPrefix && (normalise(description) === schemaPrefix || normalise(description).startsWith(`${schemaPrefix} - `));
  const fields = [
    { label: 'Entity type', aliases: ['Entity type', 'Type', 'Schema'], values: schemaAlreadyShown ? [] : [schema] },
    { label: 'Topics', aliases: ['Topics'], values: rich.topics },
    { label: 'Countries', aliases: ['Countries'], values: rich.countries },
    { label: 'Positions', aliases: ['Positions'], values: rich.positions },
    { label: 'Programmes', aliases: ['Programs', 'Programmes'], values: rich.programs },
    { label: 'Sanctions', aliases: ['Sanctions'], values: rich.sanctions },
    { label: 'Birth date', aliases: ['Birth date'], values: rich.birthDate },
  ];
  return fields.flatMap(field => {
    const values = Array.isArray(field.values) ? field.values.map(text).filter((value): value is string => !!value) : [];
    const distinct = [...new Map(values.map(value => [normalise(value), value])).values()];
    const extra = distinct.filter(value => !field.aliases.some(alias => labelled.get(normalise(alias))?.has(normalise(value))));
    return extra.length ? [{ label: field.label, values: extra }] : [];
  });
}

export function EntityDetails({ entity }: { entity: Entity }) {
  const [expandedMatch, setExpandedMatch] = React.useState<number | null>(null);
  const sectionId = React.useId();
  if (entity.reviewStatus !== 'flagged') return null;
  const matches: Array<EntityMatch & { foundIn?: MatchSighting[] }> = Array.isArray(entity.matchDetails) ? entity.matchDetails : [];

  return <section aria-labelledby={sectionId} className="max-w-full py-3 text-foreground" style={{ width: 'calc(100cqi - 2rem)', maxWidth: '100%' }}>
    <div className="mb-3">
      <h3 id={sectionId} className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        Potential matches <span className="font-normal text-muted-foreground">{matches.length}</span>
      </h3>
      <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-destructive" />Review required. Match scores describe similarity to a record.</p>
    </div>
    {!matches.length ? <p className="text-sm text-muted-foreground">Match records were not recorded for this flagged entity.</p> : <ol className="divide-y">
      {matches.map((match, index) => {
        const recordName = text(match.richDetails?.name) || text(match.details) || 'Record name unavailable';
        const description = text(match.details);
        const source = text(match.source) || 'Source unavailable';
        const recordId = text(match.richDetails?.id);
        const score = typeof match.confidence === 'number' && Number.isFinite(match.confidence) && match.confidence >= 0 && match.confidence <= 1
          ? `${(match.confidence * 100).toFixed(1)}%` : 'Unavailable';
        const fields = additionalFields(match);
        const expanded = expandedMatch === index;
        const detailsId = `${sectionId}-record-${index}`;
        const recordLabel = `${recordName}, ${source}${recordId ? `, record ${recordId}` : ''}`;
        return <li key={index} className="space-y-2 py-3 first:pt-0 last:pb-0">
          <div className="flex items-start justify-between gap-4">
            <h4 className="min-w-0 break-words text-sm font-medium">{recordName}</h4>
            <dl className="shrink-0 text-right"><dt className="text-xs text-muted-foreground">Match score</dt><dd className="mt-0.5 text-sm font-medium tabular-nums">{score}</dd></dl>
          </div>
          <dl className="flex min-w-0 flex-wrap gap-x-5 gap-y-1 text-xs" style={{ columnGap: '1.25rem', rowGap: '0.25rem' }}>
            <div className="min-w-0 max-w-full"><dt className="text-muted-foreground">Source</dt><dd className="mt-0.5 break-words">{source}</dd></div>
            {recordId && <div className="min-w-0 max-w-full"><dt className="text-muted-foreground">Record ID</dt><dd className="mt-0.5 break-all">{recordId}</dd></div>}
          </dl>
          {(match.foundIn?.length ?? 0) > 1 && <div className="text-xs">
            <p className="text-muted-foreground">Matched {match.foundIn!.length} times in this order:</p>
            <ul aria-label="Match references" className="mt-2 divide-y">
              {match.foundIn!.map(seen => <li key={`${seen.name}|${seen.document ?? ''}`} className="py-1.5"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 18rem), 1fr))', columnGap: '1rem', rowGap: '0.125rem', overflowWrap: 'anywhere' }}>
                <span className="min-w-0">
                  <Badge variant="outline" className="max-w-full gap-1.5 border-slate-200 px-2.5 py-1 text-xs font-medium text-primary">
                    <FileText aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                    <span className="min-w-0 break-words">{seen.document || 'Document not recorded'}</span>
                  </Badge>
                </span>
                <span className="min-w-0 break-words text-muted-foreground" style={{ paddingLeft: '1.375rem' }}>
                  <span className="sr-only">Name in document: </span>{seen.name}
                </span>
              </li>)}
            </ul>
          </div>}
          {description && normalise(description) !== normalise(recordName) && <p className="max-w-prose break-words text-xs leading-5 text-muted-foreground">{description}</p>}
          {fields.length > 0 && <>
            <Button type="button" variant="ghost" size="sm" className="-ml-2 h-8 gap-1.5 text-xs text-primary"
              aria-label={`${expanded ? 'Hide' : 'Show'} additional details for ${recordLabel}`}
              aria-expanded={expanded} aria-controls={detailsId}
              onClick={() => setExpandedMatch(expanded ? null : index)}>
              {expanded ? 'Hide details' : 'More details'}
              <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${expanded ? 'rotate-180' : ''}`} />
            </Button>
            {/* Keep extras mounted: the PDF exporter reveals data-state=closed content. */}
            <div id={detailsId} role="region" aria-label={`Additional details for ${recordLabel}`} data-state={expanded ? 'open' : 'closed'} className={expanded ? 'border-l-2 pl-3' : 'hidden border-l-2 pl-3'}>
              <dl className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
                {fields.map(field => <div key={field.label} className="min-w-0"><dt className="text-muted-foreground">{field.label}</dt><dd className="mt-0.5 break-words">{field.values.join(', ')}</dd></div>)}
              </dl>
            </div>
          </>}
        </li>;
      })}
    </ol>}
  </section>;
}

export default Sentry.withErrorBoundary(EntityDetails, { fallback: ErrorFallback, showDialog: true });
